import React from 'react';
import { Button } from '@/components/ui/button';
import { BookingDetailInfo, InfoItem } from '@/components/shared/booking/common/detail/info';
import { cn } from '@/lib/core/utils/cn';
import { useClientTranslation } from '@/lib/core';

export type { InfoItem };

export interface ConfirmationBannerAction {
  label: string;
  onClick: () => void;
  variant?: 'primary-solid' | 'secondary-outline' | 'tertiary-link';
}

export interface ConfirmationBannerProps {
  image?: { src?: string | null; alt?: string | null } | null;
  logo?: { src?: string | null; alt?: string | null } | null;
  firstName?: string | null;
  title: string;
  rows?: InfoItem[][];
  subtitle?: string | null;
  actions?: ConfirmationBannerAction[];
  topPanel?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  contentClassName?: string;
  style?: React.CSSProperties;
}

export function ConfirmationBanner({
  image,
  logo,
  firstName,
  title,
  rows = [],
  subtitle,
  actions = [],
  topPanel,
  children,
  className,
  contentClassName,
  style,
}: ConfirmationBannerProps) {
  const translation = useClientTranslation();

  return (
    <div
      className={cn('relative flex min-h-screen flex-col overflow-hidden bg-black', className)}
      style={style}
    >
      {/* Banner + gradient */}
      <div className="pointer-events-none absolute inset-0">
        {image?.src && (
          <img
            src={image.src}
            alt={image.alt ?? ''}
            aria-hidden="true"
            className="absolute inset-0 size-full max-w-none object-cover"
          />
        )}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(180deg, rgba(0,0,0,0.5) 12%, rgba(0,0,0,0.05) 55%, rgba(0,0,0,0.85) 80%, #000 100%)',
          }}
        />
      </div>

      {topPanel && <div className="absolute top-0 right-0 left-0 z-10">{topPanel}</div>}

      {/* Content */}
      <div
        className={cn(
          'pt-safe-area relative flex flex-1 flex-col items-center justify-end gap-8 px-4 pb-10',
          contentClassName
        )}
      >
        {/* Heading */}
        <div className="flex flex-col items-center gap-4 text-center text-white">
          {firstName && (
            <p className="font-didot text-[50px] leading-none tracking-[-3px] text-white italic">
              {firstName}
            </p>
          )}
          <p className="font-pp-neue-montreal text-[20px] leading-tight font-semibold tracking-normal uppercase">
            {title}
          </p>
        </div>

        <div className="relative flex w-full flex-col items-center justify-end gap-8">
          {/* Venue logo */}
          {logo?.src && (
            <img
              src={logo.src}
              alt={logo.alt ?? translation('App.Labels.VenueLogo')}
              className="pointer-events-none size-[224px] object-contain"
            />
          )}

          {/* Booking details */}
          <div className="flex w-full flex-col items-center gap-3">
            <BookingDetailInfo rows={rows} />
            {subtitle && (
              <p className="font-pp-neue-montreal text-[12px] font-normal tracking-[0.5px] text-white uppercase">
                {subtitle}
              </p>
            )}
          </div>

          {/* Actions */}
          {actions.length > 0 && (
            <div className="flex w-full flex-col gap-2">
              {actions.map((action, i) => (
                <Button
                  key={i}
                  variant={action.variant ?? 'primary-solid'}
                  palette="white"
                  size="lg"
                  className="w-full"
                  onClick={action.onClick}
                >
                  {action.label}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>

      {children}
    </div>
  );
}
